/**
 * Los avisos: lo que ha pasado, abajo, y se va solo.
 *
 * Nace de una decena de mutaciones invocadas con `void ...` y sin `catch` —crear, archivar y
 * borrar una asignatura, crear y borrar un cuestionario, añadir, mover y borrar una pregunta,
 * reordenar, quitar una imagen, cambiar un rol, retirar un acceso—. Cuando cualquiera de ellas
 * fallaba no pasaba absolutamente nada: ni mensaje, ni deshacer, ni rastro.
 *
 * Ahora además recoge lo que antes se pintaba **en medio de la pantalla**: el «Enviado.» del
 * apodo, el error de las cuatro cifras del PIN, el «no hay ninguna partida con ese código». Todos
 * eran sucesos, no estados, y como bloque en el flujo hacían dos cosas malas a la vez: empujar el
 * contenido justo cuando hay que pulsar algo, y quedarse ahí el resto de la sesión porque nadie
 * los limpiaba.
 *
 * Cuatro decisiones que no son de gusto:
 *
 * 1. **Dos regiones hermanas, no una anidada.** Una `assertive` para los errores y otra `polite`
 *    para lo demás, y las pegatinas de dentro NO llevan rol propio. Una región viva dentro de
 *    otra pierde: manda el ancestro, así que el error salía por la cola cortés —justo al revés de
 *    lo que hacía falta— y algunos lectores leían el texto dos veces.
 * 2. **Se pueden cerrar a mano.** Lo pide WCAG 2.2.1: nada que aparezca solo y se vaya solo puede
 *    ser la única forma de leer algo. Quien lea despacio necesita poder pararlo, y quien ya lo ha
 *    leído, quitárselo de encima.
 * 3. **Se apartan de lo que está fijo al pie.** La barra de mandos del profesor está a `z-30` y
 *    esto estaba a `z-40`: durante los ocho segundos de un error, «Terminar la partida» no se
 *    podía pulsar. Ahora la pantalla que tenga una barra fija declara `--hueco-avisos` con lo que
 *    mide, y los avisos se colocan por encima. El contenedor no recibe el ratón; solo el botón de
 *    cerrar.
 * 4. **Tope y deduplicación.** Cinco avisos tapaban media pantalla de un móvil. Se muestran los
 *    tres últimos, y repetir el mismo texto renueva el que ya está en vez de apilar otro: pulsar
 *    tres veces un botón que falla es un suceso, no tres.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Aviso, type TonoAviso } from './aviso.js';
import { Glifo } from './glifo.js';

interface Mensaje {
  readonly id: number;
  readonly texto: string;
  readonly tono: TonoAviso;
  /** Ya se está yendo: se pinta la salida y se retira al terminar. */
  readonly saliendo?: boolean;
}

/** Lo que dura la animación de salida. Es `--dur-state`, escrito aquí porque lo usa un reloj. */
const SALIDA_MS = 180;

interface Avisos {
  /** Algo ha salido bien. Se va solo a los pocos segundos. */
  readonly correcto: (texto: string) => void;
  /** Algo ha fallado. Dura más, porque hay que leerlo, e interrumpe al lector de pantalla. */
  readonly fallo: (texto: string) => void;
}

const Contexto = createContext<Avisos | null>(null);

const DURACION = {
  exito: 4000,
  error: 8000,
  /** Lo que queda tras soltar el foco o el ratón: ya se ha leído, solo hace falta cerrar el gesto. */
  tras_atencion: 1500,
} as const;

/** Cuántos caben a la vez sin tapar la pantalla de un móvil. */
const TOPE = 3;

export function ProveedorAvisos({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [mensajes, setMensajes] = useState<readonly Mensaje[]>([]);
  const siguiente = useRef(1);
  /** Los temporizadores vivos, para poder cancelarlos al cerrar a mano. */
  const relojes = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  /**
   * Retira un aviso, dejándole terminar su salida.
   *
   * Entraban animados y desaparecían de golpe, que se lee como un fallo de pintado. Se marca como
   * saliente, corre la animación y **después** sale del array: quitarlo del array primero es
   * quitar el nodo, y un nodo que no está no puede animarse.
   */
  const cerrar = useCallback((id: number) => {
    const reloj = relojes.current.get(id);
    if (reloj !== undefined) {
      clearTimeout(reloj);
      relojes.current.delete(id);
    }
    setMensajes((actuales) => actuales.map((m) => (m.id === id ? { ...m, saliendo: true } : m)));
    relojes.current.set(
      id,
      setTimeout(() => {
        relojes.current.delete(id);
        setMensajes((actuales) => actuales.filter((m) => m.id !== id));
      }, SALIDA_MS),
    );
  }, []);

  // El temporizador de expiración cierra por el MISMO camino que el botón. Si retirara el mensaje
  // por su cuenta, lo que se va solo desaparecería de golpe y solo se animaría lo que se cierra a
  // mano, que es justo al revés de lo que se ve más veces.
  const cerrarRef = useRef(cerrar);
  cerrarRef.current = cerrar;

  const anadir = useCallback((texto: string, tono: TonoAviso, duracion: number) => {
    const id = siguiente.current;
    siguiente.current += 1;

    setMensajes((actuales) => {
      // Repetir el mismo texto renueva el que ya está: pulsar tres veces un botón que falla es
      // un suceso, no tres.
      const sinRepetido = actuales.filter((m) => {
        if (m.texto !== texto) return true;
        const reloj = relojes.current.get(m.id);
        if (reloj !== undefined) clearTimeout(reloj);
        relojes.current.delete(m.id);
        return false;
      });
      return [...sinRepetido, { id, texto, tono }].slice(-TOPE);
    });

    relojes.current.set(
      id,
      setTimeout(() => cerrarRef.current(id), duracion),
    );
  }, []);

  /**
   * Un aviso no se va mientras se le está prestando atención.
   *
   * Si desaparece con el foco puesto en su botón de cerrar, el nodo enfocado sale del DOM y el
   * foco cae al `body`: quien navega con teclado vuelve al principio del documento (WCAG 2.4.3).
   * Y con el ratón es peor: si se desvanece entre el `mousedown` y el `mouseup`, el clic aterriza
   * en lo que hubiera debajo.
   *
   * Al soltar la atención se le da un plazo corto, no el original: ya ha estado en pantalla.
   */
  const retener = useCallback(() => {
    for (const reloj of relojes.current.values()) clearTimeout(reloj);
    relojes.current.clear();
  }, []);

  const soltar = useCallback(() => {
    setMensajes((actuales) => {
      for (const mensaje of actuales) {
        if (relojes.current.has(mensaje.id)) continue;
        relojes.current.set(
          mensaje.id,
          setTimeout(() => cerrarRef.current(mensaje.id), DURACION.tras_atencion),
        );
      }
      return actuales;
    });
  }, []);

  // Al desmontar no queda ningún temporizador apuntando a un estado que ya no existe.
  useEffect(() => {
    const vivos = relojes.current;
    return () => {
      for (const reloj of vivos.values()) clearTimeout(reloj);
      vivos.clear();
    };
  }, []);

  const valor = useMemo<Avisos>(
    () => ({
      correcto: (texto) => anadir(texto, 'exito', DURACION.exito),
      fallo: (texto) => anadir(texto, 'error', DURACION.error),
    }),
    [anadir],
  );

  const errores = mensajes.filter((m) => m.tono === 'error');
  const resto = mensajes.filter((m) => m.tono !== 'error');

  return (
    <Contexto.Provider value={valor}>
      {children}
      <div
        onMouseEnter={retener}
        onMouseLeave={soltar}
        onFocusCapture={retener}
        onBlurCapture={soltar}
        className="pointer-events-none fixed inset-x-0 z-40 flex flex-col items-center gap-2 p-4"
        /*
         * **La variable se declara en la RAÍZ del documento, no en el `<main>` de cada pantalla.**
         *
         * Este contenedor es HERMANO del árbol de rutas, no descendiente: el proveedor pinta
         * `{children}` y esta capa como dos hijos del mismo nodo. Las propiedades personalizadas
         * heredan hacia abajo por el árbol, nunca hacia un hermano, así que declararla en el
         * `<main>` de `/host` dejaba esto en el valor de reserva —cero— y los avisos seguían
         * pegados al borde, encima de la barra de mandos. Lo pone `useHuecoDeAvisos`, que escribe
         * en `document.documentElement`.
         */
        style={{ bottom: 'var(--hueco-avisos, 0px)' }}
      >
        {/* Dos regiones HERMANAS. Anidar una dentro de otra hace que mande la de fuera. */}
        <Region avisos={errores} interrumpe onCerrar={cerrar} />
        <Region avisos={resto} interrumpe={false} onCerrar={cerrar} />
      </div>
    </Contexto.Provider>
  );
}

function Region({
  avisos,
  interrumpe,
  onCerrar,
}: {
  avisos: readonly Mensaje[];
  interrumpe: boolean;
  onCerrar: (id: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      /*
       * **El rol va en la REGIÓN, con nombre, y no en cada pegatina.**
       *
       * En los hijos no puede ir: una región viva dentro de otra pierde —manda la de fuera—, así
       * que el error salía por la cola cortés, justo al revés de lo que promete, y algunos
       * lectores leían el texto dos veces.
       *
       * Y lleva nombre porque una pantalla puede tener sus propios avisos permanentes —el de
       * dominio no permitido, el de la cuenta eliminada—, que también son `alert`. Sin nombre, esa
       * región y esta son indistinguibles para quien navega saltando de región en región. El
       * nombre en un `div` pelado sí sería una infracción: `aria-label` está prohibido en un
       * elemento sin rol, y `alert`/`status` sí lo admiten.
       */
      role={interrumpe ? 'alert' : 'status'}
      /*
       * `aria-atomic="false"` **explícito**, porque el implícito de `alert` y `status` es `true`.
       *
       * Con el implícito, al llegar el segundo aviso el lector no lee el nuevo: relee la región
       * entera —«Avisos importantes: texto uno, descartar el aviso, texto dos, descartar el
       * aviso»—. Es el mismo problema que las dos regiones hermanas vinieron a resolver, entrando
       * por otra puerta, y con el añadido de que también se lee el nombre del botón de cerrar.
       */
      aria-atomic="false"
      aria-label={t(interrumpe ? 'common.alertsUrgent' : 'common.alerts')}
      className="flex w-full max-w-md flex-col items-center gap-2"
    >
      {avisos.map((mensaje) => (
        <Aviso
          key={mensaje.id}
          tono={mensaje.tono}
          className={`w-full shadow-lg ${mensaje.saliendo === true ? 'sale-abajo' : 'entra-abajo'}`}
          accion={
            <button
              type="button"
              onClick={() => onCerrar(mensaje.id)}
              // El único que recibe el ratón de todo el contenedor: lo demás no debe interceptar
              // nada de lo que haya debajo.
              className="rounded-chip pointer-events-auto -m-1 flex min-h-11 min-w-11 shrink-0 items-center justify-center"
            >
              <Glifo nombre="cerrar" size={18} />
              <span className="sr-only">{t('common.dismiss')}</span>
            </button>
          }
        >
          {mensaje.texto}
        </Aviso>
      ))}
    </div>
  );
}

/**
 * Avisar de cómo ha ido una acción.
 *
 * Fuera del proveedor no falla: devuelve algo que no hace nada. Así una pantalla suelta en una
 * prueba no revienta por no tener el contexto montado.
 */
export function useAvisos(): Avisos {
  const contexto = useContext(Contexto);
  return contexto ?? { correcto: () => undefined, fallo: () => undefined };
}

/**
 * Declara cuánto sitio hay que dejar libre al pie para no tapar lo que está fijo ahí.
 *
 * Va en `document.documentElement` porque la capa de avisos vive fuera del árbol de rutas: una
 * variable declarada en el `<main>` de una pantalla no llega a un hermano, y el mecanismo entero
 * se quedaba en su valor de reserva sin que nada lo delatara.
 *
 * Se limpia al desmontar: si no, la siguiente pantalla heredaría el hueco de la anterior y los
 * avisos flotarían a media altura sobre nada.
 */
export function useHuecoDeAvisos(alto: string): void {
  useEffect(() => {
    const raiz = document.documentElement;
    raiz.style.setProperty('--hueco-avisos', alto);
    return () => {
      raiz.style.removeProperty('--hueco-avisos');
    };
  }, [alto]);
}

/**
 * El hueco, **medido** en vez de adivinado.
 *
 * Devuelve una referencia que se engancha al elemento fijo al pie; su altura pasa a ser el hueco.
 *
 * Adivinarlo no vale, y está comprobado: la barra de mandos del profesor mide 112 px en un
 * portátil y 142 en un teléfono, porque los botones envuelven. Con una constante, el número
 * acierta en un ancho y falla en el otro, que es la mitad de arreglo de siempre. El observador
 * también recoge lo que cambia sin que cambie el ancho: la barra tiene botones distintos según la
 * fase de la partida.
 */
export function useHuecoMedido<T extends HTMLElement>(): React.RefObject<T | null> {
  const referencia = useRef<T>(null);

  useEffect(() => {
    const nodo = referencia.current;
    const raiz = document.documentElement;
    if (nodo === null) return;

    // Se mide `offsetHeight` y no el `contentRect` que trae la entrada: el borde superior de la
    // barra no entra en el rectángulo de contenido, y ese píxel es justo el que la tapa.
    const publicar = (): void => {
      raiz.style.setProperty('--hueco-avisos', `${String(Math.ceil(nodo.offsetHeight))}px`);
    };

    const observador = new ResizeObserver(publicar);

    observador.observe(nodo);
    publicar();

    return () => {
      observador.disconnect();
      raiz.style.removeProperty('--hueco-avisos');
    };
  }, []);

  return referencia;
}
